import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A horizontal shelf with paging chevrons.
 *
 * The scrollbar still works — the chevrons are for a mouse, which has no easy
 * horizontal scroll. Each appears only while there is somewhere left to go in
 * its direction, so the rail's edges say what the buttons would otherwise lie
 * about, and a rail short enough to fit shows no chrome at all.
 */
const Rail = ({ className = 'rail', children }) => {
  const scroller = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    measure();
    const el = scroller.current;
    if (!el) return undefined;

    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
    // Re-measured when the rail's contents change, not just on mount
  }, [measure, children]);

  const page = (direction) => {
    const el = scroller.current;
    // Most of a viewport per press: the last visible card stays on screen as
    // the anchor, the way every carousel that feels right does it.
    if (el) el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <div className="rail-wrap">
      {canLeft && (
        <button
          type="button"
          className="rail-arrow rail-arrow--left"
          aria-label="Scroll left"
          onClick={() => page(-1)}
        >
          ‹
        </button>
      )}
      <div className={className} ref={scroller}>
        {children}
      </div>
      {canRight && (
        <button
          type="button"
          className="rail-arrow rail-arrow--right"
          aria-label="Scroll right"
          onClick={() => page(1)}
        >
          ›
        </button>
      )}
    </div>
  );
};

export default Rail;
